import { ComponentProps, FC, useMemo } from 'react';

import * as SliderPrimitive from '@radix-ui/react-slider';

import { PauseIcon } from '@/assets/icons';

type SurveyQuestionSlider = ComponentProps<typeof SliderPrimitive.Root>;

export const SurveyQuestionSlider: FC<SurveyQuestionSlider> = ({
  min = 0,
  max = 10,
  step = 1,
  value = [0],
  ...props
}) => {
  const steps = useMemo(() => {
    const totalSteps = Math.floor((max - min) / step);
    return Array.from({ length: totalSteps + 1 }, (_, i) => min + i * step);
  }, [min, max, step]);
  const clampedLeft = Math.min(100, Math.max(0, ((value[0] - min) / (max - min)) * 100));

  return (
    <div className="relative w-full">
      <div
        className="absolute text-body-12-semibold text-custom-warm-grey-600 bg-custom-warm-grey-50 px-2 py-1 outline outline-custom-warm-grey-200 rounded-[6px] -top-[29px] left-1/2 -translate-x-1/2"
        style={{ left: `${clampedLeft}%` }}
      >
        {value[0]}
      </div>

      <SliderPrimitive.Root
        className="relative flex items-center w-full h-6"
        min={min}
        max={max}
        step={step}
        value={value}
        {...props}
      >
        <SliderPrimitive.Track className="relative w-full h-1 bg-custom-warm-grey-150 rounded-full">
          <SliderPrimitive.Range className="absolute h-full rounded-full" />

          {steps.map(stepValue => (
            <div
              key={stepValue}
              className="absolute top-1/2 transform -translate-y-1/2"
              style={{
                left: `${((stepValue - min) / (max - min)) * 100}%`,
              }}
            >
              {value[0] === stepValue ? null : (
                <div className="size-3 bg-white border border-custom-warm-grey-300 rounded-full" />
              )}
            </div>
          ))}
        </SliderPrimitive.Track>

        <SliderPrimitive.Thumb
          className="cursor-pointer focus:outline-none items-center justify-center"
          aria-label="Slider Thumb"
        >
          <div className="text-custom-cyan-500 border-2 border-custom-cyan-400 rounded-full p-0.5">
            <PauseIcon />
          </div>
        </SliderPrimitive.Thumb>
      </SliderPrimitive.Root>
    </div>
  );
};
